<template>
    <div class="sticky top-0 flex flex-col lg:flex-row justify-between items-start">
        <h2 class="text-xl leading-9 bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text font-medium font-headline antialiased ">Info</h2>
        <nav v-if="user.level >= 2" class="flex items-end gap-2">
            <FormButton @click="edit()" v-if="!editing" icon="fa-pen" icon-size="text-lg" prefix-class="fak" color="blue">Edit</FormButton>
            <template v-else>
                <FormButton @click="cancel()" color="gray">Cancel</FormButton>
                <FormButton @click="save" color="green">Save Changes</FormButton>
            </template>
        </nav>

    </div>
    <p class="text-sm text-bhav-cool-gray-500 bg-bhav-cool-gray-100 p-2 mt-4 rounded">
        <span class="fak fa-alert mr-2"></span>
        This data is inherited from Kova. Any changes made here will not update Kova, but will override those values.
    </p>
    <dl class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4" v-if="!editing">
        <dt class="font-normal text-bhav-blue-200 py-4 pr-4">Community Name</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal">{{ community.meta?.info?.name }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Slug</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ community.slug }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Tagline</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ community.meta?.info?.tagline }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Description</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ community.meta?.info?.description }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Address</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">
            <p v-if="community.meta?.info?.address?.street1">{{ community.meta?.info?.address?.street1 }}</p>
            <p v-if="community.meta?.info?.address?.street2">{{ community.meta?.info?.address?.street2 }}</p>
            <p v-if="community.meta?.info?.address?.city && community.meta?.info?.address?.state && community.meta?.info?.address?.zip">{{ community.meta?.info?.address?.city }} {{ community.meta?.info?.address?.state }} {{ community.meta?.info?.address?.zip }}</p>
        </dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Interest Rate</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ community?.meta?.info?.interest_rate ?? 5 }}%</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Geolocation</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">
            <p>Latitude: {{ community.meta?.info?.geo?.lat }}º</p>
            <p>Longitude: {{ community.meta?.info?.geo?.lng }}º</p>
        </dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Community Website</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50"><a class="text-bhav-blue-500 hover:underline" :href="community.meta.info.website" target="_blank" title="Visit this home's website">{{ community.meta.info.website }}</a></dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Hubspot Scheduling Link</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50"><a class="text-bhav-blue-500 hover:underline" :href="community.meta.info.hubspot_scheduling_url" target="_blank" title="Visit the scheduling website">{{ community.meta.info.hubspot_scheduling_url }}</a></dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Community Fees</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">
            <ul v-if="community.meta?.info?.service_fees?.length" class="space-y-2">
                <li v-for="(fee, index) in community.meta.info.service_fees" :key="index" class="flex gap-2">
                    <span class="font-medium">{{ getTypeLabelFromValue(fee.type || '') }}:</span>
                    <span v-if="fee.name">({{ fee.name }})</span>
                    <!-- Support both old and new format for backward compatibility -->
                    <span v-if="fee.fee && fee.frequency">
                        {{ formatToCurrency(fee.fee) }}/{{ getFrequencyUnit(fee.frequency) }}
                    </span>
                    <span v-else-if="fee.monthly_fee">{{ formatToCurrency(fee.monthly_fee) }}/month</span>
                    <span v-else-if="fee.yearly_fee">{{ formatToCurrency(fee.yearly_fee) }}/year</span>
                </li>
            </ul>
            <span v-else class="text-bhav-cool-gray-400">No service fees</span>
        </dd>
    </dl>

    <form @submit="save" class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4" v-else-if="user.level >= 2">
        <legend class="sr-only">Custom Properties</legend>

        <label for="info.name" class="font-normal text-bhav-blue-300 py-5">Community Name</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 w-full">
            <TextInput id="info.name" v-model="item.meta.info.name" maxlength="60" size="60" />
        </div>

        <label for="slug" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Slug</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 w-full">
            <TextInput id="slug" v-model="item.slug" @keyup="validateSlug" maxlength="255" size="255" />
        </div>

        <label for="tagline" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Tagline</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 w-full">
            <TextInput id="tagline" v-model="item.meta.info.tagline" maxlength="255" size="255" />
        </div>

        <label for="info.description" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Description</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 w-full">
            <textarea class="bg-bhav-cool-gray-50 text-bhav-blue-500 text-sm w-full p-3 rounded-lg focus:border-bhav-blue-400 min-h-48" id="info.description" v-model="item.meta.info.description"></textarea>
        </div>

        <label for="info.location" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Location</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 w-full">
            <SelectField id="info.location" class="w-full" v-model="item.location_id" :options="locationOptions" empty="Select a Location..." />
        </div>

        <h3 class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Community Address</h3>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 grid lg:grid-cols-3 gap-2 border-t-[2px] border-bhav-cool-gray-50">
            <p class="lg:col-span-3 grid gap-2">
                <label for="info.address.street1" class="text-sm leading-5 font-medium text-bhav-blue-300">Street Address 1</label>
                <TextInput id="info.address.street1" v-model="item.meta.info.address.street1"/>
            </p>
            <p class="lg:col-span-3 grid gap-2">
                <label for="info.address.street2" class="text-sm leading-5 font-medium text-bhav-blue-300">Street Address 2</label>
                <TextInput id="info.address.street2" v-model="item.meta.info.address.street2"/>
            </p>
            <p class="grid gap-2">
                <label for="info.address.city" class="text-sm leading-5 font-medium text-bhav-blue-300">City</label>
                <TextInput id="info.address.city" v-model="item.meta.info.address.city"/>
            </p>
            <p class="grid gap-2">
                <label for="info.address.state" class="text-sm leading-5 font-medium text-bhav-blue-300">State</label>
                <SelectField id="info.address.state" class="w-full" v-model="item.meta.info.address.state" :options="states" empty="Select a State..."/>
            </p>
            <p class="grid gap-2">
                <label for="info.address.zip" class="text-sm leading-5 font-medium text-bhav-blue-300">Zip Code</label>
                <NumberField id="info.address.zip" width="w-full" :min="0" :max="99999" :arrows="false" v-model="item.meta.info.address.zip" />
            </p>
        </div>

        <p class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Rates</p>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 grid lg:grid-cols-6 gap-2 border-t-[2px] border-bhav-cool-gray-50">
            <p class="grid gap-2">
                <label for="info.interest_rate" class="text-sm leading-5 font-medium text-bhav-blue-300">Interest Rate</label>
                <NumberField suffix="%" id="info.interest_rate" width="w-full" :max="100" :min="0" step=".01" v-model="item.meta.info.interest_rate" />
            </p>
        </div>

        <p class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Geolocation</p>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 grid lg:grid-cols-6 gap-2 border-t-[2px] border-bhav-cool-gray-50">
            <p class="grid gap-2">
                <label for="info.geo.lat" class="text-sm leading-5 font-medium text-bhav-blue-300">Latitude</label>
                <CoordinateField suffix="º" id="info.geo.lat" width="w-full" :max="90" :min="-90" v-model="item.meta.info.geo.lat" />
            </p>
            <p class="grid gap-2">
                <label for="info.geo.lng" class="text-sm leading-5 font-medium text-bhav-blue-300">Longitude</label>
                <CoordinateField suffix="º" id="info.geo.lng" width="w-full" :max="180" :min="-180" v-model="item.meta.info.geo.lng" />
            </p>
        </div>

        <label for="info.website" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Community Website</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 w-full">
            <URLField id="info.website" v-model="item.meta.info.website" width="w-full" />
        </div>

        <label for="info.hubspot_scheduling_url" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Hubspot Scheduling Link</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 w-full">
            <URLField id="info.hubspot_scheduling_url" v-model="item.meta.info.hubspot_scheduling_url" width="w-full" />
        </div>

        <h3 class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Community Fees</h3>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <div v-for="(fee, index) in item.meta.info.service_fees" :key="index" class="grid grid-cols-12 gap-4 items-end mb-4">

                <p class="grid gap-2 col-span-3">
                    <label :for="`service_fee_name_${index}`" class="text-sm leading-5 font-medium text-bhav-blue-300">Name</label>
                    <TextInput
                        :id="`service_fee_name_${index}`"
                        v-model="fee.name"
                        :class="{'border-2 border-bhav-red-500': showServiceFeeErrors && !fee.name?.trim()}" />
                </p>

                <p class="grid gap-2 col-span-3">
                    <label :for="`service_fee_type_${index}`" class="text-sm leading-5 font-medium text-bhav-blue-300">Type</label>
                    <SelectField
                        :id="`service_fee_type_${index}`"
                        class="w-full"
                        v-model="fee.type"
                        :options="getAvailableTypes(index)"
                        empty="Select a Type..."
                        :class="{'border-2 border-bhav-red-500': showServiceFeeErrors && !fee.type}" />
                </p>

                <p class="grid gap-2 col-span-2">
                    <label :for="`service_fee_frequency_${index}`" class="text-sm leading-5 font-medium text-bhav-blue-300">Frequency</label>
                    <SelectField
                        :id="`service_fee_frequency_${index}`"
                        class="w-full"
                        :allow-null="false"
                        v-model="fee.frequency"
                        :options="[
                            { value: 'monthly', label: 'Monthly' },
                            { value: 'quarterly', label: 'Quarterly' },
                            { value: 'yearly', label: 'Yearly' }
                        ]"
                        :class="{'border-2 border-bhav-red-500': showServiceFeeErrors && !fee.frequency}" />
                </p>

                <p class="grid gap-2 col-span-3">
                    <label :for="`service_fee_amount_${index}`" class="text-sm leading-5 font-medium text-bhav-blue-300">Fee Amount</label>
                    <NumberField
                        :id="`service_fee_amount_${index}`"
                        prefix="$"
                        width="w-full"
                        :arrows="false"
                        step="0.01"
                        :min="0"
                        v-model="fee.fee"
                        :class="{'border-2 border-bhav-red-500': showServiceFeeErrors && !fee.fee}" />
                </p>

                <div class="col-span-1 flex items-end pb-1">
                    <FormButton
                        @click="removeServiceFee(index)"
                        color="red"
                        icon="fa-trash"
                        icon-size="text-lg"
                        prefix-class="fak" />
                </div>
            </div>

            <FormButton
                @click="addServiceFee"
                color="blue"
                icon="fa-plus"
                icon-size="text-lg"
                prefix-class="fak">
                Add Service Fee
            </FormButton>
        </div>

    </form>
       
</template>

<script>
import axios from 'axios';
import { formatToCurrency } from '@/helpers/numberHelpers';
import {FormButton, URLField, SelectField, NumberField, CoordinateField, TextInput} from '@/components/form';
import {states, serviceFeeTypes} from '@/helpers/globals';
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { useMessageStore } from '@/stores/message';

export default {
    data() {
        return {
            item: {},
            states,
            serviceFeeTypes,
            editing: false,
            showServiceFeeErrors: false,
            locationOptions: []
        }
    },
    methods: {

        getTypeLabelFromValue(typeValue) {
            const type = this.serviceFeeTypes.find(t => t.value === typeValue);
            return type ? type.label : typeValue;
        },

        // Unit shown after a fee amount in the read-only view (e.g. "$97/quarter")
        getFrequencyUnit(frequency) {
            return { monthly: 'month', quarterly: 'quarter', yearly: 'year' }[frequency] ?? 'year';
        },

        loadLocations() {
            axios.get('locations')
                .then(res => {
                    this.locationOptions = res.data.flatMap(parent =>
                        parent.locations.map(child => ({
                            value: child.id,
                            label: `${parent.name} › ${child.name}`
                        }))
                    );
                });
        },
        edit() {
            if(this.user.level < 2) return;
            this.editing = true;
            this.showServiceFeeErrors = false;
            this.item = {...this.community};
        },
        cancel() {
            this.editing = false;
            this.showServiceFeeErrors = false;
        },
        save() {
            if(this.user.level < 2) return;

            // Validate service fees
            if (!this.allServiceFeesValid) {
                this.showServiceFeeErrors = true;
                this.setAlert({
                    text: 'Each community fee must have a name, type, fee amount, and frequency.',
                    level: 'error'
                });
                return;
            }
            if(this.item.meta?.info?.interest_rate === 0) this.item.meta.info.interest_rate = null;
            this.$emit('update', this.item);
            this.cancel();
        },
        isServiceFeeValid(fee) {
            const hasName = fee.name && fee.name.trim() !== '';
            const hasType = fee.type !== null && fee.type !== '';
            const hasFee = fee.fee !== null && fee.fee !== undefined && fee.fee > 0;
            const hasFrequency = ['monthly', 'quarterly', 'yearly'].includes(fee.frequency);
            return hasName && hasType && hasFee && hasFrequency;
        },
        addServiceFee() {
            this.item.meta.info.service_fees.push({
                name: '',
                type: null,
                fee: null,
                frequency: 'monthly'
            });
        },
        removeServiceFee(index) {
            this.item.meta.info.service_fees.splice(index, 1);
        },
        validateSlug(slug) {
            let clean = slug.target.value.replace(/[^A-Za-z0-9-]/,'');
            slug.target.value = clean;
        },
        formatToCurrency,
        ...mapActions(useMessageStore, ['setAlert'])
    },
    computed: {
        ...mapState(useAuthStore, ['user']),
        allServiceFeesValid() {
            if (!this.item.meta.info.service_fees?.length) return true;
            return this.item.meta.info.service_fees.every(fee => this.isServiceFeeValid(fee));
        },
        // Get available types for each fee (filter out already selected)
        getAvailableTypes() {
            return (currentIndex) => {
                const usedTypes = this.item.meta.info.service_fees
                    .map((fee, index) => index !== currentIndex ? fee.type : null)
                    .filter(type => type !== null);

                return this.serviceFeeTypes.filter(type =>
                    !usedTypes.includes(type.value)
                );
            };
        }
    },
    emits: [
        'update'
    ],
    props: {
        community: {
            type: Object,
            required: true
        }
    },
    created() {
        this.loadLocations();
    },
    components: {
        FormButton,
        URLField,
        SelectField,
        NumberField,
        CoordinateField,
        TextInput
    }
}
</script>